import { supabase } from "@/shared/lib/supabaseClient";
import type {
  Database,
  EscalaSituacao,
  EscalaParticipacaoSituacao,
  EscalaResposta,
  EscalaOrigemResposta,
  EscalaHistoricoTipo,
} from "@/shared/types/database.types";

/**
 * Camada de dados do módulo Escala (Fase 3 — frontend definitivo, 27/09/2026).
 *
 * Regras que este service NUNCA reimplementa (moram inteiras no banco):
 * - Autorização de escrita: sempre `lidera_ou_gerencia_ministerio(ministerio_id)`,
 *   nunca `gerencia_modulo`/flags globais de Eventos/Reservas (arquitetura §4/§6).
 * - Visibilidade de leitura: RLS de `escalas`/`escala_participacoes`/`escala_historico`
 *   (funções `pode_ver_escala`/`pode_ver_historico_escala`) — este service nunca
 *   filtra por igreja/ministério no cliente, só reflete o que a RLS já devolveu.
 * - Equipe de uma escala (γ — ESC-DEC-006, migrations 0093/0094): a lista de
 *   participantes vem SEMPRE da função `escala_listar_participantes`, nunca da
 *   tabela `escala_participacoes` — para o membro comum a tabela devolve só a
 *   própria linha; a função devolve a equipe (nome/função) e mascara no banco
 *   o status e os dados dos colegas (`respostaVisivel`). Este service não
 *   decide o que é visível: só reflete as colunas que a função devolveu.
 * - `escala_participacoes`/`escala_historico` não têm NENHUM grant de escrita
 *   direto para `authenticated` — toda escrita passa pelas 9 RPCs abaixo. Este
 *   service nunca faz `.insert()/.update()/.delete()` nessas tabelas.
 * - Confirmação automática (migration 0087), rodada/versão otimista, conflito
 *   (aviso não bloqueante) e notificações (D10/D11, trigger já existente) são
 *   decisões do backend — o service só lê e reflete o que o RPC devolveu/gravou.
 */

export type Escala = Omit<Database["public"]["Tables"]["escalas"]["Row"], "situacao"> & {
  situacao: EscalaSituacao;
};

export type EscalaParticipacaoRow = Omit<
  Database["public"]["Tables"]["escala_participacoes"]["Row"],
  "situacao" | "resposta" | "origem_resposta"
> & {
  situacao: EscalaParticipacaoSituacao;
  resposta: EscalaResposta;
  origem_resposta: EscalaOrigemResposta | null;
};

export type EscalaHistoricoEvento = Omit<
  Database["public"]["Tables"]["escala_historico"]["Row"],
  "tipo"
> & {
  tipo: EscalaHistoricoTipo;
};

export interface MinhaParticipacao {
  id: string;
  funcaoNome: string | null;
  resposta: EscalaResposta;
  origemResposta: EscalaOrigemResposta | null;
}

export interface EscalaComRelacoes extends Escala {
  ministerioNome: string;
  /** Participação ativa da pessoa logada nesta escala, se houver (RLS já garante que só vem a dela). */
  minhaParticipacao: MinhaParticipacao | null;
  /**
   * `true` só quando a leitura das participações da pessoa deu certo. Sem isso,
   * `minhaParticipacao === null` não prova que ela está fora da equipe (a leitura
   * pode ter falhado) e a lista não deve dizer "Você não está na equipe".
   */
  participacoesLidas: boolean;
}

export interface ParticipanteDetalhe {
  id: string;
  /** `true` na linha da pessoa logada — vem do banco; não derivar comparando `pessoaId`. */
  ehPropria: boolean;
  /** Preenchido só na própria linha e para quem administra/lidera/gerencia; `null` nos colegas vistos por membro comum. */
  pessoaId: string | null;
  nome: string;
  funcaoId: string | null;
  funcaoNome: string | null;
  /** `false` = o banco não liberou o status deste participante a quem consulta: não exibir selo de resposta. */
  respostaVisivel: boolean;
  resposta: EscalaResposta | null;
  origemResposta: EscalaOrigemResposta | null;
  justificativa: string | null;
}

export interface EscalaDetalhe {
  escala: EscalaComRelacoes;
  /** Só participações `ativa` — a equipe atual. Remoções ficam só no histórico. */
  participantes: ParticipanteDetalhe[];
}

export interface MinisterioParaEscala {
  id: string;
  nome: string;
}

export interface CandidatoParticipante {
  pessoaId: string;
  nome: string;
  funcaoId: string;
  funcaoNome: string;
}

/** Equipe da escala mais recente do ministério — base do atalho "Repetir equipe" na criação. */
export interface UltimaEquipe {
  escalaNome: string;
  /** ISO da data/hora da escala de origem. */
  dataHora: string;
  itens: ItemParaAdicionar[];
}

export interface DadosCriarEscala {
  ministerioId: string;
  nome: string;
  dataHora: string; // ISO
  descricao?: string | null;
  /** Imutável após a criação (Documento de Requisitos, cap. 5) — sem tela de alteração posterior. */
  exigeConfirmacao: boolean;
}

export interface DadosEditarEscala {
  versao: number;
  nome?: string;
  descricao?: string | null;
  dataHora?: string;
}

export interface ItemParaAdicionar {
  pessoaId: string;
  funcaoId: string;
}

/**
 * Traduz o erro de uma RPC do Escala para uma mensagem amigável. As RPCs já
 * levantam `RAISE EXCEPTION` com texto em PT-BR pronto para exibição (padrão
 * confirmado lendo o corpo real das 9 funções no DSV) — este helper só cobre
 * os casos em que o erro NÃO vem da RPC (rede, RLS bruta numa leitura).
 */
function traduzErroEscala(error: { code?: string; message?: string } | null, fallback: string): Error {
  if (!error) return new Error(fallback);
  if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
    return new Error(
      "Você não tem permissão para esta ação. Só quem lidera ou gerencia o ministério dono da escala pode fazer isso.",
    );
  }
  // As RPCs de Escala já devolvem mensagem pronta em PT-BR (RAISE EXCEPTION) —
  // repassar sem reescrever evita esconder informação útil (ex.: "Conflito de
  // versão...", "Justificativa obrigatória ao recusar").
  return new Error(error.message || fallback);
}

interface LinhaEscalaListagem {
  id: string;
  igreja_id: string;
  ministerio_id: string;
  nome: string;
  descricao: string | null;
  data_hora: string;
  exige_confirmacao: boolean;
  situacao: string;
  rodada: number;
  versao: number;
  motivo_cancelamento: string | null;
  cancelado_em: string | null;
  cancelado_por_pessoa_id: string | null;
  criado_por_pessoa_id: string | null;
  created_at: string;
  updated_at: string;
  ministerios: { nome: string } | null;
}

export const escalaService = {
  /**
   * Todas as escalas visíveis para a pessoa logada (RLS já filtra: dono,
   * visão ampla, participação própria — arquitetura §6/§9). Sem filtro de
   * frontend por igreja/ministério; abas "Agendadas"/"Realizadas"/"Canceladas" e o filtro
   * por ministério são recortes client-side sobre este mesmo conjunto já
   * autorizado (não uma segunda consulta).
   */
  async listar(pessoaId: string): Promise<EscalaComRelacoes[]> {
    const [escalasRes, minhasParticipacoesRes] = await Promise.all([
      supabase
        .from("escalas")
        .select("*, ministerios(nome)")
        .order("data_hora", { ascending: false }),
      supabase
        .from("escala_participacoes")
        .select("id, escala_id, funcao_id, nome_funcao_snapshot, resposta, origem_resposta")
        .eq("pessoa_id", pessoaId)
        .eq("situacao", "ativa"),
    ]);
    if (escalasRes.error) throw new Error("Não foi possível carregar as escalas.");

    const minhaPorEscala = new Map<string, MinhaParticipacao>();
    if (!minhasParticipacoesRes.error) {
      for (const p of (minhasParticipacoesRes.data ?? []) as Array<{
        id: string;
        escala_id: string;
        funcao_id: string | null;
        nome_funcao_snapshot: string | null;
        resposta: string;
        origem_resposta: string | null;
      }>) {
        minhaPorEscala.set(p.escala_id, {
          id: p.id,
          funcaoNome: p.nome_funcao_snapshot,
          resposta: p.resposta as EscalaResposta,
          origemResposta: p.origem_resposta as EscalaOrigemResposta | null,
        });
      }
    }

    const linhas = (escalasRes.data ?? []) as unknown as LinhaEscalaListagem[];
    return linhas.map((e) => ({
      ...e,
      situacao: e.situacao as EscalaSituacao,
      ministerioNome: e.ministerios?.nome ?? "—",
      minhaParticipacao: minhaPorEscala.get(e.id) ?? null,
      participacoesLidas: !minhasParticipacoesRes.error,
    }));
  },

  /**
   * Detalhe de uma escala + equipe atual (participações `ativa`). `null` se
   * a RLS não devolver linha — o chamador deve tratar "não encontrado" e
   * "sem permissão" da MESMA forma visualmente (MOV-027/033, nunca vazar
   * diferença entre os dois casos).
   *
   * A equipe vem de `escala_listar_participantes` (γ — ESC-DEC-006): só
   * participações ativas, ordenadas por nome; para o membro comum, status,
   * justificativa, `pessoa_id` e `funcao_id` dos colegas já chegam nulos e
   * `respostaVisivel` = false. Falha ao listar a equipe mantém o
   * comportamento anterior (lista vazia), sem derrubar o detalhe da escala.
   */
  async detalhe(escalaId: string): Promise<EscalaDetalhe | null> {
    const [escalaRes, participantesRes] = await Promise.all([
      supabase.from("escalas").select("*, ministerios(nome)").eq("id", escalaId).maybeSingle(),
      supabase.rpc("escala_listar_participantes", { p_escala_id: escalaId }),
    ]);
    if (escalaRes.error || !escalaRes.data) return null;

    const e = escalaRes.data as unknown as LinhaEscalaListagem;
    // Os tipos gerados tratam todas as colunas de `RETURNS TABLE` como não nulas;
    // aqui os campos mascarados pelo banco são tipados como nuláveis de verdade.
    const participantes = ((participantesRes.data ?? []) as unknown as Array<{
      id: string;
      eh_propria: boolean;
      pessoa_id: string | null;
      funcao_id: string | null;
      nome: string;
      funcao_nome: string | null;
      resposta_visivel: boolean;
      resposta: string | null;
      origem_resposta: string | null;
      justificativa: string | null;
    }>).map((p) => ({
      id: p.id,
      ehPropria: p.eh_propria,
      pessoaId: p.pessoa_id,
      nome: p.nome,
      funcaoId: p.funcao_id,
      funcaoNome: p.funcao_nome,
      respostaVisivel: p.resposta_visivel,
      resposta: p.resposta as EscalaResposta | null,
      origemResposta: p.origem_resposta as EscalaOrigemResposta | null,
      justificativa: p.justificativa,
    }));

    return {
      escala: {
        ...e,
        situacao: e.situacao as EscalaSituacao,
        ministerioNome: e.ministerios?.nome ?? "—",
        minhaParticipacao: null, // resolvido pelo chamador a partir de `participantes` (campo `ehPropria`)
        participacoesLidas: false, // só a lista (`listar`) usa este campo
      },
      participantes,
    };
  },

  /** Só deve ser chamado quando `podeVerHistorico(escalaId)` já confirmou `true` — a RLS também barra por conta própria. */
  async historico(escalaId: string): Promise<EscalaHistoricoEvento[]> {
    const { data, error } = await supabase
      .from("escala_historico")
      .select("*")
      .eq("escala_id", escalaId)
      .order("created_at", { ascending: false });
    if (error) throw new Error("Não foi possível carregar o histórico desta escala.");
    return (data ?? []) as unknown as EscalaHistoricoEvento[];
  },

  /** `pode_ver_historico_escala` (RPC já existente) — decide se a aba/tela de histórico aparece. */
  async podeVerHistorico(escalaId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("pode_ver_historico_escala", { p_escala_id: escalaId });
    if (error) return false;
    return Boolean(data);
  },

  /**
   * `lidera_ou_gerencia_ministerio` (RPC já existente, DEC-02) — autoridade
   * real de escrita sobre QUALQUER escala deste ministério. Nunca substituir
   * por `gerencia_modulo`/flags globais de Eventos/Reservas.
   */
  async podeGerir(ministerioId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("lidera_ou_gerencia_ministerio", {
      p_ministerio_id: ministerioId,
    });
    if (error) return false;
    return Boolean(data);
  },

  /**
   * Ministérios em que a pessoa logada pode CRIAR uma nova escala: precisa
   * liderar/gerenciar (mesmo critério de `lidera_ou_gerencia_ministerio`) E o
   * ministério precisa estar `ativo=true` — reflexo, só no frontend, da
   * decisão de produto formalizada em 27/09/2026 ("DEC — Ministério ativo
   * obrigatório para criação de Escala"). A regra AINDA NÃO é aplicada no
   * backend (`escala_criar` não valida `ministerios.ativo` hoje) — este
   * filtro é puramente de UX (evita oferecer uma opção que a decisão de
   * produto já fechou como inválida); a checagem real, quando implementada,
   * continuará sendo a autoridade de fato.
   */
  async ministeriosParaCriar(pessoaId: string): Promise<MinisterioParaEscala[]> {
    const { data: vinculos, error } = await supabase
      .from("pessoa_ministerio")
      .select("ministerio_id, funcao_id")
      .eq("pessoa_id", pessoaId)
      .eq("status", "ativo");
    if (error) throw new Error("Não foi possível carregar seus ministérios.");
    if (!vinculos?.length) return [];

    const { data: funcoes, error: erroFuncoes } = await supabase
      .from("funcoes_ministeriais")
      .select("id")
      .in("id", [...new Set(vinculos.map((v) => v.funcao_id))])
      .or("eh_lider.eq.true,pode_gerenciar_modulo.eq.true");
    if (erroFuncoes) throw new Error("Não foi possível verificar suas funções.");
    const funcoesComAutoridade = new Set((funcoes ?? []).map((f) => f.id));

    const ministerioIds = [
      ...new Set(vinculos.filter((v) => funcoesComAutoridade.has(v.funcao_id)).map((v) => v.ministerio_id)),
    ];
    if (!ministerioIds.length) return [];

    const { data: ministerios, error: erroMinisterios } = await supabase
      .from("ministerios")
      .select("id, nome")
      .in("id", ministerioIds)
      .eq("ativo", true)
      .order("nome");
    if (erroMinisterios) throw new Error("Não foi possível carregar seus ministérios.");
    return ministerios ?? [];
  },

  /**
   * Candidatos elegíveis para entrar na escala: integrantes ATIVOS do
   * ministério dono, com a função que exercem nele (mesma checagem que
   * `escala_adicionar_participantes` fará no servidor — "Pessoa não possui a
   * função informada, ativa, neste ministério"). Com `escalaId`, exclui quem já
   * tem participação ativa nela; sem `escalaId` (criação de escala, que ainda
   * não existe) devolve todos os integrantes. Mesma composição já usada em
   * `ministeriosService.detalhe()` para listar integrantes de um ministério.
   */
  async candidatosParticipantes(ministerioId: string, escalaId?: string): Promise<CandidatoParticipante[]> {
    const [vinculosRes, jaAtivosRes] = await Promise.all([
      supabase
        .from("pessoa_ministerio")
        .select("pessoa_id, funcao_id, pessoas(nome_completo), funcoes_ministeriais(nome)")
        .eq("ministerio_id", ministerioId)
        .eq("status", "ativo"),
      escalaId
        ? supabase.from("escala_participacoes").select("pessoa_id").eq("escala_id", escalaId).eq("situacao", "ativa")
        : Promise.resolve({ data: [] as Array<{ pessoa_id: string }>, error: null }),
    ]);
    if (vinculosRes.error) throw new Error("Não foi possível carregar os integrantes do ministério.");

    const jaAtivos = new Set(
      ((jaAtivosRes.data ?? []) as Array<{ pessoa_id: string }>).map((p) => p.pessoa_id),
    );

    return (
      (vinculosRes.data ?? []) as unknown as Array<{
        pessoa_id: string;
        funcao_id: string;
        pessoas: { nome_completo: string } | null;
        funcoes_ministeriais: { nome: string } | null;
      }>
    )
      .filter((v) => !jaAtivos.has(v.pessoa_id) && v.pessoas)
      .map((v) => ({
        pessoaId: v.pessoa_id,
        nome: v.pessoas!.nome_completo,
        funcaoId: v.funcao_id,
        funcaoNome: v.funcoes_ministeriais?.nome ?? "—",
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  },

  /**
   * Nomes de pessoas citadas no histórico (quem executou, quem foi afetado).
   * Leitura em `pessoas`, que a RLS já restringe à igreja e a quem o usuário
   * pode ver: o que não vier na resposta (pessoa fora do alcance, administração
   * da plataforma) fica SEM nome e a tela mostra um rótulo genérico — nunca se
   * tenta descobrir o nome por outro caminho. Melhor esforço: falha aqui não
   * derruba o histórico.
   */
  async nomesPessoas(pessoaIds: string[]): Promise<Record<string, string>> {
    const ids = [...new Set(pessoaIds)];
    if (!ids.length) return {};
    const { data, error } = await supabase.from("pessoas").select("id, nome_completo, eh_admin_fantasma").in("id", ids);
    if (error) return {};
    const nomes: Record<string, string> = {};
    for (const p of (data ?? []) as Array<{ id: string; nome_completo: string; eh_admin_fantasma: boolean | null }>) {
      // Administração fantasma nunca aparece com nome no histórico, mesmo que a RLS devolva a linha.
      if (p.eh_admin_fantasma) continue;
      nomes[p.id] = p.nome_completo;
    }
    return nomes;
  },

  /**
   * Equipe da escala ativa mais recente do ministério que tenha participantes
   * (olha as 3 mais recentes). Só serve de atalho na criação de escala: a
   * seleção resultante ainda passa pela mesma validação do servidor. A equipe
   * vem de `escala_listar_participantes`; para quem lidera/gerencia o ministério
   * (único perfil que cria escala) `pessoa_id`/`funcao_id` vêm preenchidos.
   * Melhor esforço: qualquer falha = sem atalho.
   */
  async ultimaEquipe(ministerioId: string): Promise<UltimaEquipe | null> {
    const { data: escalas, error } = await supabase
      .from("escalas")
      .select("id, nome, data_hora")
      .eq("ministerio_id", ministerioId)
      .eq("situacao", "ativa")
      .order("data_hora", { ascending: false })
      .limit(3);
    if (error) return null;

    for (const e of (escalas ?? []) as Array<{ id: string; nome: string; data_hora: string }>) {
      const { data, error: erroEquipe } = await supabase.rpc("escala_listar_participantes", { p_escala_id: e.id });
      if (erroEquipe) return null;
      const itens = ((data ?? []) as unknown as Array<{ pessoa_id: string | null; funcao_id: string | null }>)
        .filter((p) => p.pessoa_id && p.funcao_id)
        .map((p) => ({ pessoaId: p.pessoa_id!, funcaoId: p.funcao_id! }));
      if (itens.length) return { escalaNome: e.nome, dataHora: e.data_hora, itens };
    }
    return null;
  },

  /** Funções do ministério dono — usado para escolher a nova função ao mover um participante (`escala_alterar_funcao` recusa função de outro ministério). */
  async funcoesMinisterio(ministerioId: string): Promise<{ id: string; nome: string }[]> {
    const { data, error } = await supabase
      .from("funcoes_ministeriais")
      .select("id, nome")
      .eq("ministerio_id", ministerioId)
      .order("nome");
    if (error) throw new Error("Não foi possível carregar as funções deste ministério.");
    return data ?? [];
  },

  // ---- Escrita: sempre via RPC, nunca DML direto (as 3 tabelas não têm grant de escrita para `authenticated`). ----

  async criar(dados: DadosCriarEscala): Promise<string> {
    const { data, error } = await supabase.rpc("escala_criar", {
      p_ministerio_id: dados.ministerioId,
      p_nome: dados.nome,
      p_data_hora: dados.dataHora,
      // Os tipos gerados pelo Supabase para argumentos opcionais de RPC usam
      // `| undefined` (nunca `| null`) — mesmo quando o parâmetro SQL tem
      // `DEFAULT NULL`. Passar `null` explicitamente falha o typecheck.
      p_descricao: dados.descricao ?? undefined,
      p_exige_confirmacao: dados.exigeConfirmacao,
    });
    if (error || !data) throw traduzErroEscala(error, "Não foi possível criar a escala.");
    return data;
  },

  async editar(escalaId: string, dados: DadosEditarEscala): Promise<void> {
    const { error } = await supabase.rpc("escala_editar", {
      p_escala_id: escalaId,
      p_versao: dados.versao,
      p_nome: dados.nome ?? undefined,
      p_descricao: dados.descricao ?? undefined,
      p_data_hora: dados.dataHora ?? undefined,
    });
    if (error) throw traduzErroEscala(error, "Não foi possível editar a escala.");
  },

  async cancelar(escalaId: string, versao: number, motivo?: string | null): Promise<void> {
    const { error } = await supabase.rpc("escala_cancelar", {
      p_escala_id: escalaId,
      p_versao: versao,
      p_motivo: motivo ?? undefined,
    });
    if (error) throw traduzErroEscala(error, "Não foi possível cancelar a escala.");
  },

  async adicionarParticipantes(escalaId: string, itens: ItemParaAdicionar[]): Promise<string[]> {
    const { data, error } = await supabase.rpc("escala_adicionar_participantes", {
      p_escala_id: escalaId,
      p_participantes: itens.map((i) => ({ pessoa_id: i.pessoaId, funcao_id: i.funcaoId })),
    });
    if (error) throw traduzErroEscala(error, "Não foi possível adicionar os participantes.");
    return data ?? [];
  },

  async removerParticipante(participacaoId: string): Promise<void> {
    const { error } = await supabase.rpc("escala_remover_participante", {
      p_participacao_id: participacaoId,
    });
    if (error) throw traduzErroEscala(error, "Não foi possível remover o participante.");
  },

  async alterarFuncao(participacaoId: string, novaFuncaoId: string): Promise<void> {
    const { error } = await supabase.rpc("escala_alterar_funcao", {
      p_participacao_id: participacaoId,
      p_nova_funcao_id: novaFuncaoId,
    });
    if (error) throw traduzErroEscala(error, "Não foi possível alterar a função.");
  },

  /** Resposta da PRÓPRIA pessoa (`escala_responder` exige `pessoa_id_atual() = participacao.pessoa_id`). */
  async responder(
    participacaoId: string,
    resposta: Extract<EscalaResposta, "CONFIRMADA" | "RECUSADA">,
    justificativa?: string | null,
  ): Promise<void> {
    const { error } = await supabase.rpc("escala_responder", {
      p_participacao_id: participacaoId,
      p_resposta: resposta,
      p_justificativa: justificativa ?? undefined,
    });
    if (error) throw traduzErroEscala(error, "Não foi possível registrar sua resposta.");
  },

  /** Resposta registrada pela liderança em nome do participante (D10 se recusa) — nunca chamar em vez de `responder` para a própria pessoa. */
  async registrarRespostaAdministrativa(
    participacaoId: string,
    resposta: Extract<EscalaResposta, "CONFIRMADA" | "RECUSADA">,
    justificativa?: string | null,
  ): Promise<void> {
    const { error } = await supabase.rpc("escala_registrar_resposta_administrativa", {
      p_participacao_id: participacaoId,
      p_resposta: resposta,
      p_justificativa: justificativa ?? undefined,
    });
    if (error) throw traduzErroEscala(error, "Não foi possível registrar a resposta administrativa.");
  },

  /** Nunca chamar pessoa por pessoa — sempre em lote (o RPC já aceita array). Aviso não bloqueante, nunca erro. */
  async verificarConflito(escalaId: string, pessoaIds: string[]): Promise<Record<string, boolean>> {
    if (!pessoaIds.length) return {};
    const { data, error } = await supabase.rpc("escala_verificar_conflito", {
      p_escala_id: escalaId,
      p_pessoa_ids: pessoaIds,
    });
    if (error) return {}; // aviso é best-effort — falha em verificar não deve travar o fluxo de adicionar
    const mapa: Record<string, boolean> = {};
    for (const linha of data ?? []) mapa[linha.pessoa_id] = linha.tem_conflito;
    return mapa;
  },
};
